import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface PlanLimits {
  formsCount: number;
  submissionsCount: number;
  formsLimit: number;
  submissionsLimit: number;
  canCreateForm: boolean;
  canUploadFiles: boolean;
  hasAIInsights: boolean; // New: AI summaries and insights
  maxUploadsPerForm: number; // Limit files per form
  maxFileSize: number; // Limit file size
  formsRemaining: number;
  isLoading: boolean;
  userPlan: string;
}

export const usePlanLimits = () => {
  const { user } = useAuth();
  const [limits, setLimits] = useState<PlanLimits>({
    formsCount: 0,
    submissionsCount: 0,
    formsLimit: 0,
    submissionsLimit: 0,
    canCreateForm: false,
    canUploadFiles: false,
    hasAIInsights: false,
    maxUploadsPerForm: 0,
    maxFileSize: 5 * 1024 * 1024, // 5MB default
    formsRemaining: 0,
    isLoading: true,
    userPlan: 'free'
  });

  useEffect(() => {
    const fetchLimits = async () => {
      if (!user) {
        setLimits(prev => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('plan')
          .eq('user_id', user.id)
          .single();

        const userPlan = (profile?.plan || 'free').toLowerCase();
        
        // Define capabilities locally based on plan
        const hasPaidPlan = ['starter', 'pro', 'business'].includes(userPlan);
        const hasAIInsights = hasPaidPlan; // Upgrade to Starter for insights
        const canUploadFiles = hasPaidPlan || userPlan === 'free'; // Free allowed 1 file
        
        let maxUploadsPerForm = 0;
        if (userPlan === 'starter') maxUploadsPerForm = 5;
        else if (['pro', 'business'].includes(userPlan)) maxUploadsPerForm = 100; // Effectively unlimited
        else if (userPlan === 'free') maxUploadsPerForm = 1;

        const { data, error } = await supabase.rpc('check_plan_limits', {
          user_id_param: user.id,
          plan_type: userPlan
        });

        if (error) {
          console.error('Error checking plan limits:', error);
          return;
        }

        if (data && data.length > 0) {
          const result = data[0];
          setLimits({
            formsCount: Number(result.forms_count),
            submissionsCount: Number(result.submissions_count),
            formsLimit: result.forms_limit,
            submissionsLimit: result.submissions_limit,
            canCreateForm: result.can_create_form,
            canUploadFiles,
            hasAIInsights,
            maxUploadsPerForm,
            maxFileSize: 5 * 1024 * 1024,
            formsRemaining: result.forms_remaining,
            isLoading: false,
            userPlan
          });
        }
      } catch (error) {
        console.error('Error fetching plan limits:', error);
        setLimits(prev => ({ ...prev, isLoading: false }));
      }
    };

    fetchLimits();
  }, [user]);

  return limits;
};